import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '@utils/api';
import { getColorVariations, getSizeVariations } from '@utils/product';
import analytics from '@stone/utils/analytics';
import useMediaQueries from '@stone/hooks/useMediaQueries';
import ErrorModal from '@components/error/ErrorModal';
import ModalDialog from '@stone/components/modal/ModalDialog';
import ProductBoutiqueStoreChooser from './ProductBoutiqueStoreChooser';
import ProductAddToBag from '../product/ProductActionAddToBag';
import ProductBoutiqueReview from './ProductBoutiqueReview';
import ProductBoutiqueReserve from './ProductBoutiqueReserve';
import Button from '@stone/ui/button/Button';
import LoginOrRegisterColumn from '../login/LoginOrRegisterColumn';
import BagInfoBox from '../bag/BagInfoBox';
import { siteUrl } from '@common/utils/urlutils';
import Icon from '@stone/ui/icon/Icon';
const ProductTileReview = React.lazy(() => import('../product/ProductTileReview'));
const ProductTileReviewExpanded = React.lazy(() => import('../product/ProductTileReviewExpanded'));
const ProductTileReserve = React.lazy(() => import('../product/ProductTileReserve'));
const ProductBoutiqueModal = (props) => {
    const { t } = useTranslation();
    const { isMobile } = useMediaQueries();
    // -- INITS
    // non renderizzare la modale all'apertura della pagina, non fare chiamate se non aperta
    const firstRender = useRef(true);
    const [loggedIn, setLoggedIn] = useState(window.loggedin);
    const [customer, setCustomer] = useState();
    useEffect(() => {
        if (props.isOpen) {
            setCurrentSize(props.size);
            if (firstRender.current) {
                firstRender.current = false;
                api.getClickAndReserveConfig().then((data) => {
                    if (data.limitOrdersEnabled) {
                        setClickAndReserveEnabled(true);
                    }
                });
            }
            if (loggedIn) {
                let val = {};
                !customer &&
                    api.getForm('reserve', val).then((data) => {
                        let _customer = {
                            firstName: data.firstname.htmlValue,
                            lastName: data.lastname.htmlValue,
                            phone: data.phone.htmlValue,
                            phonePrefix: data.phonePrefix.htmlValue,
                            email: data.email.htmlValue,
                        };
                        setCustomer(_customer);
                        val.applicationorigin = window.appmode ? 'app' : 'web';
                        setInitialValues(val);
                        setFormdef(data);
                    });
                !countries &&
                    api.getBoutiqueCountries().then((data) => {
                        setCountries(data);
                    });
            }
        }
    }, [props.isOpen, loggedIn]);
    const [currentStore, setCurrentStore] = useState();
    const [requestSent, setRequestSent] = useState();
    const [clickAndReserveEnabled, setClickAndReserveEnabled] = useState(false);
    // --- PRODUCT STATES
    const [product, setProduct] = useState();
    const [currentColor, setCurrentColor] = useState();
    const [currentSize, setCurrentSize] = useState();
    // -- STATES
    const [initialValues, setInitialValues] = useState();
    const [formdef, setFormdef] = useState();
    const [countries, setCountries] = useState(null);
    const [days, setDays] = useState({});
    const [errorMessage, setErrorMessage] = useState();
    // -- MEMO
    const addToBagEnabled = useMemo(() => {
        return !!product && !!(currentSize === null || currentSize === void 0 ? void 0 : currentSize.selectable) && !product.isBoutiqueExclusive && !currentColor.isBoutiqueExclusive && !product.notShoppable && !product.isPreview;
    }, [currentSize, product, currentColor]);
    const productSelected = useMemo(() => {
        return currentColor && currentSize ? `${currentColor.productId}${currentSize.id}` : null;
    }, [currentColor, currentSize]);
    // const hasRangePrice = useMemo(() => {
    //   return product?.price?.type === 'range';
    // }, [product]);
    // --- METHODS
    const changeProduct = (productid) => {
        api.getProduct(productid).then((data) => {
            var _a;
            setProduct(data);
            //set new color
            const selectedColor = ((_a = getColorVariations(data)) === null || _a === void 0 ? void 0 : _a.find((v) => v.selected)) || null;
            setCurrentColor(selectedColor);
            //set new size
            const selectedSize = getSizeVariations(data).find((v) => v.selected) || null;
            setCurrentSize(selectedSize);
        });
    };
    const setSizeCallback = (val) => {
        changeProduct(`${product.masterId}${currentColor.id}${val.id}`);
        return false;
    };
    const setColorCallback = (val) => {
        changeProduct(`${val.productId}${(currentSize === null || currentSize === void 0 ? void 0 : currentSize.id) || ''}`);
    };
    // -- CALLBACKS
    const resetOnClose = useCallback(() => {
        props.onRequestClose();
        setTimeout(() => {
            setRequestSent(undefined);
            setCurrentStore(undefined);
            setCustomer(undefined);
        }, 1000);
    }, [props.onRequestClose]);
    const noResults = useCallback(() => {
        return (React.createElement("div", { className: "product-reserve__detail__text", role: "alert", "aria-live": "polite" }, addToBagEnabled ? (React.createElement(React.Fragment, null,
            React.createElement("p", null, t('findinboutique.notavailableinboutique')),
            React.createElement(ProductAddToBag, { hierarchy: 'primary', product: product, size: currentSize, pushAnalytics: () => analytics.addToBag(product, currentSize, null, { detail: 'reserve in store' }), callback: resetOnClose, updateErrorMessage: (msg) => setErrorMessage(msg) }),
            React.createElement(ErrorModal, { message: errorMessage, onClose: () => setErrorMessage(undefined) }))) : (React.createElement(React.Fragment, null,
            React.createElement("p", { className: "alert" },
                React.createElement(Icon, { name: "alert", family: '10' }),
                " ",
                t('findinboutique.notavailable'))))));
    }, [currentSize, product, errorMessage, addToBagEnabled]);
    // --- EFFECTS
    useEffect(() => {
        if (props.product) {
            setProduct(props.product);
        }
        if (props.color) {
            setCurrentColor(props.color);
        }
        if (props.size) {
            setCurrentSize(props.size);
        }
    }, [props.product, props.color, props.size]);
    return (React.createElement(ModalDialog, { id: "reserveinboutsique", className: "product-reserve--modal lg-scrollable", closeInvertedSm: true, isOpen: props.isOpen, onAfterOpen: () => {
            var _a;
            analytics.reserveInStore(((_a = props.color) === null || _a === void 0 ? void 0 : _a.productId) || props.product.id);
        }, onRequestClose: resetOnClose, contentLabel: props.title, fullWidth: isMobile, fullHeight: isMobile, noPadding: true },
        React.createElement("div", { className: "product-reserve" },
            React.createElement("div", { className: "product-reserve__main" }, !loggedIn || !!currentStore ? (React.createElement(ProductTileReview, { imageSize: "small", product: product, color: props.color, size: loggedIn && currentSize })) : isMobile ? (React.createElement(ProductTileReserve, { product: product, onChangeColor: (colorVariation) => {
                    setColorCallback(colorVariation);
                    return false;
                }, onChangeSize: (sizeVariation) => {
                    setSizeCallback(sizeVariation);
                    return false;
                }, setSizeguideOpen: props === null || props === void 0 ? void 0 : props.setSizeGuideOpen })) : (React.createElement(ProductTileReviewExpanded, { product: product, color: currentColor, size: currentSize, onChangeColor: (colorVariation) => {
                    setColorCallback(colorVariation);
                }, onSizeChange: (sizeVariation) => {
                    setSizeCallback(sizeVariation);
                }, colourVariations: props.colourVariations, sizeVariations: props.sizeVariations, setSizeGuideOpen: props === null || props === void 0 ? void 0 : props.setSizeGuideOpen }))),
            React.createElement("div", { className: "product-reserve__detail" },
                React.createElement("div", { className: "divider divier--small" }),
                clickAndReserveEnabled && React.createElement("p", { className: "dialog_subtitle bold" }, t('findinboutique.minimumreservation')),
                !loggedIn && (React.createElement(React.Fragment, null,
                    React.createElement("div", { className: "login-include" },
                        React.createElement(LoginOrRegisterColumn, { closeModal: resetOnClose, updateLoggeedStatus: () => setLoggedIn(true), updateLoggedin: () => setLoggedIn(true), from: "reserve", hideElements: ['mainHeading', 'wishlistbutton'], slots: {
                                passwordheading: React.createElement("h3", { className: "login-modal-subtitle mb-12" }, t('account.welcomeback')),
                                benefitsFallback: (React.createElement(BagInfoBox, { className: "mt-0", title: t('store.clickAndReserve.infobox.title'), text: t('store.clickAndReserve.infobox.text'), buttonText: t('store.clickAndReserve.infobox.cta'), buttonAction: () => {
                                        location.href = siteUrl('register');
                                    }, heading: "h2" })),
                            }, componentContext: "include", rememberme: true, reloadComponent: true })))),
                customer && (React.createElement(React.Fragment, null, !currentStore ? (
                //STEP 1
                React.createElement(ProductBoutiqueStoreChooser, { countries: countries, productSelected: productSelected, noResults: noResults(), onStoreSelect: setCurrentStore })) : !requestSent ? (
                //STEP 2
                React.createElement(ProductBoutiqueReserve, { product: product, customer: customer, currentColor: currentColor, currentSize: currentSize, formdef: formdef, initialValues: initialValues, currentStore: currentStore, onStoreReset: () => {
                        setCurrentStore(null);
                    }, onSubmit: (data, values) => {
                        analytics.reserveInStore(currentColor.productId, 'submit');
                        setRequestSent(values);
                    }, setDays: setDays })) : (
                //STEP 3
                React.createElement(ProductBoutiqueReview, { requestSent: requestSent, formdef: formdef, product: product, currentColor: currentColor, currentSize: currentSize, currentStore: currentStore, days: days },
                    React.createElement(Button, { type: "button", cls: ['button--primary'], className: "full", onClick: resetOnClose }, t('button.backtoshopping'))))))))));
};
export default ProductBoutiqueModal;
